// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — File Library Store (Zustand)
// Manages documents and categories for the File Library module
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchTable, createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../lib/api-switch';
import { useAuthStore } from './authStore';

// ── Helper ──────────────────────────────────────────────────────

function getToken(): string | undefined {
  return useAuthStore.getState().token ?? undefined;
}

// ── Types ───────────────────────────────────────────────────────

export interface FLDocument {
  id: number;
  Rid: number;
  title: string;
  description: string;
  category: string;
  tags: string;
  fileUrl: string;
  fileType: string;
  publishDate: string;
  expirationDate: string;
  status: string;
  uploadedBy: string;
  version: string;
  priority: string;
  fileUploadUrl: string;
}

export interface FLCategory {
  id: number;
  Rid: number;
  name: string;
  icon: string;
  color: string;
  sortOrder: string;
  active: string;
}

// ── Mappers ─────────────────────────────────────────────────────

const FD = FIELDS.flDocuments;
const FC = FIELDS.flCategories;

function mapDocument(row: Record<string, unknown>): FLDocument {
  const id = Number(row.record_id ?? row.id) || 0;
  return {
    id,
    Rid: id,
    title: (row[FD.title] as string) ?? '',
    description: (row[FD.description] as string) ?? '',
    category: (row[FD.categoryId] as string) ?? '',
    tags: (row[FD.tags] as string) ?? '',
    fileUrl: (row[FD.file] as string) ?? '',
    fileType: '',
    publishDate: (row[FD.publishedAt] as string) ?? '',
    expirationDate: '',
    status: (row[FD.status] as string) ?? 'published',
    uploadedBy: (row[FD.uploadedBy] as string) ?? '',
    version: '',
    priority: '',
    fileUploadUrl: (row[FD.file] as string) ?? '',
  };
}

function mapCategory(row: Record<string, unknown>): FLCategory {
  const id = Number(row.record_id ?? row.id) || 0;
  return {
    id,
    Rid: id,
    name: (row[FC.name] as string) ?? '',
    icon: '',
    color: (row[FC.color] as string) ?? '',
    sortOrder: (row[FC.sortOrder] as string) ?? '',
    active: 'true',
  };
}

// ── Store Interface ─────────────────────────────────────────────

interface FileLibraryStore {
  documents: FLDocument[];
  categories: FLCategory[];
  loading: boolean;
  error: string | null;

  loadDocuments: () => Promise<void>;
  createDocument: (data: Partial<FLDocument>) => Promise<FLDocument | null>;
  updateDocument: (id: number, data: Partial<FLDocument>) => Promise<void>;
  deleteDocument: (id: number) => Promise<void>;
  uploadFile: (id: number, file: File) => Promise<void>;

  loadCategories: () => Promise<void>;
  createCategory: (data: Partial<FLCategory>) => Promise<FLCategory | null>;
  updateCategory: (id: number, data: Partial<FLCategory>) => Promise<void>;
  deleteCategory: (id: number) => Promise<void>;
}

// ── Store ───────────────────────────────────────────────────────

export const useFileLibraryStore = create<FileLibraryStore>((set, get) => ({
  documents: [],
  categories: [],
  loading: false,
  error: null,

  // ── Documents ───────────────────────────────────────────────

  loadDocuments: async () => {
    set({ loading: true, error: null });
    try {
      const token = getToken();
      const data = await fetchTable(TABLES.flDocuments, { token });
      const documents = data.map((row: Record<string, unknown>) => mapDocument(row));
      set({ documents, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load documents' });
    }
  },

  createDocument: async (data) => {
    const token = getToken();
    const F = FIELDS.flDocuments;
    // Ensure publishedAt is a full ISO timestamp — V2 Postgres rejects date-only strings
    const rawDate = data.publishDate ?? new Date().toISOString();
    const publishedAt = /^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? `${rawDate}T00:00:00Z` : rawDate;
    const fields: Array<[string, string]> = [
      [F.title, data.title ?? ''],
      [F.description, data.description ?? ''],
      [F.categoryId, data.category ?? ''],
      [F.tags, data.tags ?? ''],
      [F.status, data.status ?? 'published'],
      [F.uploadedBy, data.uploadedBy ?? ''],
      [F.publishedAt, publishedAt],
    ];
    const rid = await createRecord(TABLES.flDocuments, fields, token);
    await get().loadDocuments();
    return get().documents.find(d => d.id === rid) ?? null;
  },

  updateDocument: async (id, data) => {
    const token = getToken();
    const F = FIELDS.flDocuments;
    const fields: Array<[string, string]> = [];
    if (data.title !== undefined) fields.push([F.title, data.title]);
    if (data.description !== undefined) fields.push([F.description, data.description]);
    if (data.category !== undefined) fields.push([F.categoryId, data.category]);
    if (data.tags !== undefined) fields.push([F.tags, data.tags]);
    if (data.status !== undefined) fields.push([F.status, data.status]);
    if (data.uploadedBy !== undefined) fields.push([F.uploadedBy, data.uploadedBy]);
    if (data.publishDate !== undefined) {
      const d = data.publishDate;
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00Z` : d;
      fields.push([F.publishedAt, iso]);
    }
    if (fields.length > 0) {
      await updateRecord(TABLES.flDocuments, id, fields, token);
      await get().loadDocuments();
    }
  },

  deleteDocument: async (id) => {
    const token = getToken();
    await deleteRecord(TABLES.flDocuments, id, token);
    set({ documents: get().documents.filter(d => d.id !== id) });
  },

  uploadFile: async (id, file) => {
    const token = getToken();
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    // Upload file data via updateRecord
    const F = FIELDS.flDocuments;
    await updateRecord(TABLES.flDocuments, id, [[F.file, base64]], token ?? undefined);
    await get().loadDocuments();
  },

  // ── Categories ──────────────────────────────────────────────

  loadCategories: async () => {
    set({ loading: true, error: null });
    try {
      const token = getToken();
      const data = await fetchTable(TABLES.flCategories, { token });
      const categories = data.map((row: Record<string, unknown>) => mapCategory(row));
      set({ categories, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load categories' });
    }
  },

  createCategory: async (data) => {
    const token = getToken();
    const F = FIELDS.flCategories;
    // F.icon and F.active are not in the V2 schema — only send fields that exist
    const fields: Array<[string, string]> = [
      [F.name, data.name ?? ''],
      [F.color, data.color ?? ''],
      [F.sortOrder, data.sortOrder ?? ''],
    ];
    if (F.description) fields.push([F.description, '']);
    const rid = await createRecord(TABLES.flCategories, fields, token);
    await get().loadCategories();
    return get().categories.find(c => c.id === rid) ?? null;
  },

  updateCategory: async (id, data) => {
    const token = getToken();
    const F = FIELDS.flCategories;
    const fields: Array<[string, string]> = [];
    if (data.name !== undefined) fields.push([F.name, data.name]);
    if (data.color !== undefined) fields.push([F.color, data.color]);
    if (data.sortOrder !== undefined) fields.push([F.sortOrder, data.sortOrder]);
    if (fields.length > 0) {
      await updateRecord(TABLES.flCategories, id, fields, token);
      await get().loadCategories();
    }
  },

  deleteCategory: async (id) => {
    const token = getToken();
    await deleteRecord(TABLES.flCategories, id, token);
    set({ categories: get().categories.filter(c => c.id !== id) });
  },
}));
